// src/components/TeamPendingTasksView.jsx
//
// The rows behind a team's PENDING number on the dashboard.
//
// That number was a dead end: it told you Database had 59 outstanding tasks and
// gave you nowhere to go with it. Answering "which 59?" meant opening Task
// Assignment, filtering by team, filtering by status, and trusting that the two
// screens meant the same thing by "pending".
//
// This was a dialog first, on the reasoning that it is a question ABOUT the
// dashboard — you want the answer and then you want the dashboard back. In use
// it turned out to be a screen people WORK from rather than glance at: a
// backlog of forty tasks read across ten columns, searched, regrouped and
// scrolled. A dialog capped at 1080px was the wrong shape for that, and the
// dashboard behind it was in the way rather than reassuring. It is a screen now
// (pages/TeamPendingTasks.jsx), which the tab bar makes cheap — the dashboard
// stays mounted in its own tab, so going back costs nothing and lands you where
// you left off, which was the dialog's one real advantage.
//
// This file is the body only: head, tools, table, footer. The page supplies the
// team and the frame around it.
//
// A TABLE rather than stacked cards, because every row carries the same six
// facts. Cards let each row wrap where it liked, so status, age and date landed
// in a different place on every line and the eye had to re-find them each time.
// Columns put them in one place, which is what lets you read straight down
// "who is unclaimed" or "what is stale" without reading a single task name.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "./Loader";
import Modal from "./Modal";
/* The same dialog the Development Tasks screen opens, from the same file —
   this list is where "what IS this task" gets asked just as often. */
import TaskDetailContent from "./TaskDetailContent";
import { teamPendingTasksApis } from "../redux/dashboard/dashboardActions";
import { useTabRefresh } from "../context/TabsContext";
import { taskTimelineApis } from "../redux/team/teamActions";
import {
  selectTaskTimeline,
  selectTaskTimelineStatus,
} from "../redux/team/teamSelectors";
import { getTeamColors, getStatusColor } from "../utils/badgeColors";
import {
  formatDateOnly,
  formatDateTimeDisplay,
  targetDateLabel,
  targetDateState,
  toDateOnly,
} from "../utils/common";
import TargetDateChips from "./TargetDateChips";
import useTargetDateFilter from "../hooks/useTargetDateFilter";
import "../assets/css/teamPending.css";

/* Oldest first is how the server sends them, and it is the order a backlog is
   read in — so it stays the order INSIDE every group, whatever the grouping.
   The rest are groupings rather than sorts: asking "by client" and getting one
   long list silently re-ordered answers the question far worse than sections
   with their own headings and counts do. */
const GROUPS = [
  /* "No grouping", not "Oldest first". Every mode is oldest-first inside its
     sections, so naming this one after the sort implied the others were not —
     what actually separates them is whether the list is broken into sections
     at all. */
  { key: "none", label: "No grouping", field: null, icon: "bi-list-ul" },
  {
    key: "client",
    label: "Client wise",
    field: "ClientName",
    empty: "No client",
    head: "Client",
    many: "clients",
    icon: "bi-building",
  },
  {
    key: "status",
    label: "Status wise",
    field: "Status",
    empty: "No status",
    head: "Status",
    many: "statuses",
    icon: "bi-flag",
  },
  {
    key: "assignee",
    label: "Person wise",
    field: "AssigneeName",
    empty: "Unclaimed",
    head: "Person",
    many: "people",
    icon: "bi-person",
  },
];

/* Widths are fixed for everything with a known shape — a serial, a task
   number, an age, a date — and the task name takes whatever is left. Left to
   itself the browser sizes every column to its widest cell, so one long client
   name shunts the whole grid sideways and the columns stop lining up between
   one grouping and the next.

   `groupedBy` drops a column when it is the field being grouped on: the value
   is already the heading above every row underneath it, so the column would
   repeat one word down the entire screen. */
const COLUMNS = [
  /* Widths are fixed for everything with a known shape and the DESCRIPTION
     takes the slack, not the task name.
     *
     * It was the other way round, and the arithmetic went against it: eight
     * fixed columns came to 1016px of a 1120px table, so the one auto column
     * — Task — was left with about a hundred, and every task name wrapped to
     * five or six lines while Module and Assigned To sat two-thirds empty.
     * A name is a few words; a description is a sentence. The sentence should
     * get the room. */
  { key: "sno", label: "S.No", width: "48px", cls: "is-sno" },
  { key: "no", label: "#", width: "64px", cls: "is-no" },
  { key: "task", label: "Task", width: "190px", cls: "is-wrap" },
  { key: "desc", label: "Description", width: "auto", cls: "is-wrap" },
  { key: "status", label: "Status", width: "108px", groupedBy: "status" },
  { key: "client", label: "Client", width: "140px", groupedBy: "client" },
  { key: "form", label: "Module", width: "110px" },
  { key: "assignee", label: "Assigned to", width: "126px", groupedBy: "assignee" },
  { key: "age", label: "Age", width: "56px", cls: "is-num" },
  { key: "date", label: "Raised", width: "92px", cls: "is-date" },
  /* Beside Raised, and after it, because the pair reads as a sentence: came
     in on the 27th, wanted by the 5th. Blank for anything raised before the
     field existed, and for work nobody has committed to a date on yet — this
     dialog is where that gap is worth seeing, since it lists exactly the
     tasks still waiting. */
  { key: "target", label: "Target", width: "92px", cls: "is-date" },
];

const TeamPendingTasksView = ({ team }) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  /* Opens grouped by client.
   *
   * A flat list of 39 answers "which tasks", and the question people bring to
   * a team's backlog is "who is waiting" — the client is the first thing they
   * scan for, and ungrouped it is one column among seven.
   *
   * Every team's card opens the same way, so the shape of the answer does not
   * change depending on which number was clicked. */
  const [groupBy, setGroupBy] = useState("client");
  const [menuOpen, setMenuOpen] = useState(false);
  /* Two readings of the same rows. "Tasks" is the backlog itself; "Summary"
     is one row per client — the counting you would otherwise do by scrolling
     the sections and reading their badges one at a time. */
  const [view, setView] = useState("tasks");
  const pickerRef = useRef(null);

  /* The task whose details are open. Null the rest of the time — the dialog is
     read-only, so there is nothing here to write back into the list. */
  const [detailTask, setDetailTask] = useState(null);

  /* Bumped when this tab is re-activated. The fetch below is keyed on the
     team, which does not change on a return visit — so the counter is what
     re-runs it. */
  const [reloadKey, setReloadKey] = useState(0);
  useTabRefresh(() => setReloadKey((k) => k + 1));

  /* Everything that has happened to that task. Asked for when the dialog opens
     rather than alongside the list: one round trip per task looked at, against
     one per task on the screen. */
  const timeline = useSelector(selectTaskTimeline);
  const timelineStatus = useSelector(selectTaskTimelineStatus);

  useEffect(() => {
    if (detailTask?.QueryCode) {
      dispatch(taskTimelineApis({ queryCode: detailTask.QueryCode }));
    }
  }, [detailTask?.QueryCode, dispatch]);

  const isNoTeam = team?.TeamCode == null;

  useEffect(() => {
    if (!team) return undefined;
    let alive = true;
    setLoading(true);

    dispatch(
      teamPendingTasksApis(
        isNoTeam ? { noTeam: 1 } : { teamCode: team.TeamCode },
      ),
    )
      .unwrap()
      .then((res) => {
        if (!alive) return;
        /* successHandler wraps once and axios wraps again, and which level
           carries the rows has differed by endpoint before. */
        const body = res?.data ?? res;
        const inner = body?.data ?? body;
        setRows(Array.isArray(inner) ? inner : []);
      })
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false));

    /* The dialog can be closed while the request is in flight, and a team
       switched before it lands. Without this the late response would paint
       one team's rows under another team's heading. */
    return () => {
      alive = false;
    };
  }, [dispatch, team, isNoTeam, reloadKey]);

  /* Escape closes the group picker. It used to close the dialog too, once the
     menu was already shut; on a page there is nothing left for it to close, so
     it now does the one job. */
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  /* mousedown, not click: a click that lands outside should shut the menu
     before whatever it landed on reacts. */
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const mode = useMemo(
    () => GROUPS.find((g) => g.key === groupBy) ?? GROUPS[0],
    [groupBy],
  );

  const columns = useMemo(
    () => COLUMNS.filter((c) => c.groupedBy !== groupBy),
    [groupBy],
  );

  /* Every task in this dialog is pending by definition, so no status is
     passed — nothing here can be closed, and asking would only let a stale
     Status field rule out a row that IS late. */
  const {
    rows: onTarget,
    counts: targetCounts,
    mode: targetMode,
    toggle: toggleTarget,
  } = useTargetDateFilter(rows, { status: () => undefined });

  /* Filter first, then section. Both the search and the grouping read the
     rows the server already put in oldest-first order, and nothing here
     re-sorts within a section — so a group's own list is still a backlog,
     read top to bottom.

     The target chips narrow the list before the search does, so the two
     compose: press Overdue, then type a client, and you get that client's
     late work. */
  const matched = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return onTarget;
    return onTarget.filter((r) =>
      [r.TaskName, r.ClientName, r.FormName, r.AssigneeName, r.Status, r.QueryNo]
        .some((v) => String(v ?? "").toLowerCase().includes(term)),
    );
  }, [onTarget, query]);

  const sections = useMemo(() => {
    if (!mode.field) return [{ key: "", label: null, items: matched }];

    const byKey = new Map();
    matched.forEach((r) => {
      const raw = r[mode.field];
      const label = String(raw ?? "").trim() || mode.empty;
      if (!byKey.has(label)) byKey.set(label, []);
      byKey.get(label).push(r);
    });

    return [...byKey.entries()]
      .map(([label, items]) => ({ key: label, label, items }))
      /* Biggest section first — "which client is waiting on the most" is the
         question grouping gets used for. The empty bucket is pinned to the
         top whatever its size: unclaimed work, or a task with no client, is
         the row somebody has to act on rather than merely read. */
      .sort((a, b) => {
        const aEmpty = a.label === mode.empty;
        const bEmpty = b.label === mode.empty;
        if (aEmpty !== bEmpty) return aEmpty ? -1 : 1;
        return b.items.length - a.items.length || a.label.localeCompare(b.label);
      });
  }, [matched, mode]);

  /* How many sections each mode would produce, shown against it in the menu.
     "Client wise · 9" says what you are about to get; without it choosing a
     grouping is a guess you have to make and then undo. */
  const groupCounts = useMemo(() => {
    const out = {};
    GROUPS.forEach((g) => {
      if (!g.field) return;
      const seen = new Set();
      matched.forEach((r) =>
        seen.add(String(r[g.field] ?? "").trim() || g.empty),
      );
      out[g.key] = seen.size;
    });
    return out;
  }, [matched]);

  /* The tally under the table. Row by row it answers "which tasks"; this
     answers "what is this backlog made of" — the shape you would otherwise
     have to reconstruct by scrolling and counting. It reads the FILTERED
     rows, so it is a summary of what is on screen rather than a constant. */
  const summary = useMemo(() => {
    const byStatus = new Map();
    let unclaimed = 0;
    let stale = 0;
    let oldest = 0;

    matched.forEach((r) => {
      const label = String(r.Status ?? "").trim() || "No status";
      byStatus.set(label, (byStatus.get(label) ?? 0) + 1);
      if (!r.AssigneeCode) unclaimed += 1;
      const age = Number(r.AgeDays) || 0;
      if (age > 30) stale += 1;
      if (age > oldest) oldest = age;
    });

    return {
      statuses: [...byStatus.entries()].sort((a, b) => b[1] - a[1]),
      unclaimed,
      stale,
      oldest,
    };
  }, [matched]);

  /* The summary counts by whatever the picker is set to, so the two views
     stay one question asked twice. "No grouping" has nothing to count by, so
     it falls back to client — the breakdown people come here for. */
  const summaryMode = mode.field ? mode : GROUPS[1];

  /* One row per client: total, a column per status, unclaimed, and the oldest
     task in that client's pile. Statuses come from `summary` so the columns
     are the ones actually present — a fixed set would print an empty "On
     Hold" column down every team that never uses it. */
  const breakdown = useMemo(() => {
    const statusCols = summary.statuses.map(([label]) => label);
    const byKey = new Map();

    matched.forEach((r) => {
      const label =
        String(r[summaryMode.field] ?? "").trim() || summaryMode.empty;
      if (!byKey.has(label)) {
        byKey.set(label, {
          label,
          total: 0,
          unclaimed: 0,
          oldest: 0,
          byStatus: {},
        });
      }
      const row = byKey.get(label);
      row.total += 1;
      const st = String(r.Status ?? "").trim() || "No status";
      row.byStatus[st] = (row.byStatus[st] ?? 0) + 1;
      if (!r.AssigneeCode) row.unclaimed += 1;
      const age = Number(r.AgeDays) || 0;
      if (age > row.oldest) row.oldest = age;
    });

    /* Same order as the sections in the task view — biggest first, with the
       empty bucket pinned to the top — so switching views does not reshuffle
       the client you were looking at. */
    const items = [...byKey.values()].sort((a, b) => {
      const aEmpty = a.label === summaryMode.empty;
      const bEmpty = b.label === summaryMode.empty;
      if (aEmpty !== bEmpty) return aEmpty ? -1 : 1;
      return b.total - a.total || a.label.localeCompare(b.label);
    });

    return { statusCols, items };
  }, [matched, summaryMode, summary.statuses]);

  const matchCount = matched.length;
  const unclaimed = rows.filter((r) => !r.AssigneeCode).length;
  const colors = getTeamColors(team?.TeamName);

  /* One cell per column, keyed the way the header is, so dropping a column
     needs one edit and the body follows. */
  const renderCell = (key, r, sno) => {
    switch (key) {
      case "sno":
        return <span className="tpt__sno">{sno}</span>;

      case "no":
        return <span className="tpt__no">#{r.QueryNo}</span>;

      case "task":
        /* No title attribute: the cell shows the whole name now, so a
           tooltip repeating it is a tooltip that says nothing.

           The name is the handle for the whole row — the same click that opens
           a task on Task Assignment and Development Tasks, so it behaves the
           way it does everywhere else. A button rather than a clickable span,
           so it is reachable by keyboard and announced as something that
           does something. */
        return (
          <button
            type="button"
            className="tpt__name tpt__name--link"
            onClick={() => setDetailTask(r)}
            title="Open task details"
          >
            {r.TaskName}
          </button>
        );

      case "desc":
        return r.NatureofQuery ? (
          <span className="tpt__desc">{r.NatureofQuery}</span>
        ) : (
          <span className="tpt__dash">—</span>
        );

      case "status":
        return (
          <span className="tpt__status" style={{ color: getStatusColor(r.Status) }}>
            <i className="bi bi-circle-fill" />
            {r.Status}
          </span>
        );

      case "client":
        return r.ClientName ? (
          <span className="tpt__cut" title={r.ClientName}>
            {r.ClientName}
          </span>
        ) : (
          <span className="tpt__dash">—</span>
        );

      case "form":
        return r.FormName ? (
          <span className="tpt__chip" title={r.FormName}>
            {r.FormName}
          </span>
        ) : (
          <span className="tpt__dash">—</span>
        );

      case "assignee":
        return (
          <span
            className={`tpt__who${r.AssigneeName ? "" : " is-none"}`}
            title={r.AssigneeName || "Unclaimed"}
          >
            <i className={`bi ${r.AssigneeName ? "bi-person-fill" : "bi-person-dash"}`} />
            {r.AssigneeName || "Unclaimed"}
          </span>
        );

      case "age":
        /* The one number worth colouring: a month-old pending task is the
           reason this dialog gets opened. */
        return (
          <span className={`tpt__age${r.AgeDays > 30 ? " is-stale" : ""}`}>
            {r.AgeDays}d
          </span>
        );

      case "date":
        return formatDateTimeDisplay(r.QueryDate, "DD MMM YYYY", "");

      case "target": {
        if (!toDateOnly(r.TargetDate)) {
          return <span className="tpt__dash">—</span>;
        }

        /* Every task in this dialog is pending by definition, so a date that
           has arrived always means something has to happen — the colour never
           cries wolf here. */
        const state = targetDateState(r.TargetDate);
        const note = targetDateLabel(r.TargetDate);

        return (
          <span
            className={`tpt__target${state ? ` is-${state}` : ""}`}
            title={note || undefined}
          >
            {formatDateOnly(r.TargetDate, "")}
            {note && <small className="tpt__target-note">{note}</small>}
          </span>
        );
      }

      default:
        return null;
    }
  };

  if (!team) return null;

  return (
    <div className="tpt is-page">
      <div className="tpt__box">
        <div className="tpt__head">
          <span
            className="tpt__badge"
            style={{ background: colors.text }}
          >
            {team.ShortName || "--"}
          </span>
          <div className="tpt__titles">
            <h6>{team.TeamName} — pending</h6>
            <p>
              {loading
                ? "Reading…"
                : `${rows.length} task${rows.length === 1 ? "" : "s"}`}
              {!loading && unclaimed > 0 && (
                <span className="tpt__unclaimed"> · {unclaimed} unclaimed</span>
              )}
            </p>
          </div>

          {/* The shape of the backlog, on the same line as its name.
           *
           * It was in the footer, which on a list of forty meant it was only
           * ever read by somebody who had already scrolled past everything it
           * summarises — and "what am I looking at" is a question wanted
           * before the looking, not after it. The header is where the answer
           * belongs: "Web App — pending, 28 tasks" and the breakdown of those
           * 28 are one thought, and the band beside the title was empty.
           *
           * Permanently visible without being sticky. The card is a flex
           * column and only .tpt__wrap scrolls, so everything above it stays
           * put by construction — no z-index, and no offset to keep in step
           * with the two sticky rows inside the table.
           *
           * Hidden in the summary view, where the totals row says all of this
           * already; printing the same numbers twice on one screen invites
           * the reader to check whether they agree. */}
          {!loading && rows.length > 0 && matchCount > 0 && view === "tasks" && (
            <div className="tpt__summary tpt__summary--head">
              {summary.statuses.map(([label, n]) => (
                <span className="tpt__stat" key={label}>
                  <i
                    className="bi bi-circle-fill"
                    style={{ color: getStatusColor(label) }}
                  />
                  {label}
                  <b>{n}</b>
                </span>
              ))}

              {summary.unclaimed > 0 && (
                <span className="tpt__stat is-warn">
                  <i className="bi bi-person-dash" />
                  Unclaimed
                  <b>{summary.unclaimed}</b>
                </span>
              )}

              {summary.stale > 0 && (
                <span className="tpt__stat is-stale">
                  <i className="bi bi-clock-history" />
                  Over 30d
                  <b>{summary.stale}</b>
                </span>
              )}

              {summary.oldest > 0 && (
                <span className="tpt__stat">
                  <i className="bi bi-hourglass-bottom" />
                  Oldest
                  <b>{summary.oldest}d</b>
                </span>
              )}
            </div>
          )}
        </div>

        <div className="tpt__tools">
          {/* Two tabs rather than a summary bolted under the table: the
              breakdown needs the full width to be a table at all, and it is a
              different question from "which tasks", not a footnote to it. */}
          <TargetDateChips
            counts={targetCounts}
            mode={targetMode}
            onToggle={toggleTarget}
          />

          <div className="tpt__views" role="tablist" aria-label="View">
            {[
              { key: "tasks", label: "Tasks", icon: "bi-list-task" },
              { key: "summary", label: "Summary", icon: "bi-bar-chart-line" },
            ].map((v) => (
              <button
                key={v.key}
                type="button"
                role="tab"
                aria-selected={view === v.key}
                className={`tpt__view${view === v.key ? " is-active" : ""}`}
                onClick={() => setView(v.key)}
              >
                <i className={`bi ${v.icon}`} />
                {v.label}
              </button>
            ))}
          </div>

          <span className="tpt__search">
            <i className="bi bi-search" />
            <input
              value={query}
              placeholder="Search task, client, person…"
              onChange={(e) => setQuery(e.target.value)}
            />
          </span>

          {/* A native select rendered as whatever the OS felt like — a grey
              Windows box with a system arrow, sitting next to a rounded search
              field it was meant to match. It also could not say what it does:
              "Client wise" alone reads as a filter, and the menu it opened had
              no room for the group counts that make the choice obvious. */}
          <div className="tpt__picker" ref={pickerRef}>
            <button
              type="button"
              className={`tpt__picker-btn${menuOpen ? " is-open" : ""}`}
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
            >
              <i className={`bi ${mode.icon} tpt__picker-icon`} />
              <span className="tpt__picker-label">
                <em>Group by</em>
                {mode.label}
              </span>
              <i className="bi bi-chevron-down tpt__picker-caret" />
            </button>

            {menuOpen && (
              <div className="tpt__menu" role="listbox" aria-label="Group by">
                {GROUPS.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    role="option"
                    aria-selected={groupBy === g.key}
                    className={`tpt__menu-item${groupBy === g.key ? " is-active" : ""}`}
                    onClick={() => {
                      setGroupBy(g.key);
                      setMenuOpen(false);
                    }}
                  >
                    <i className={`bi ${g.icon}`} />
                    <span>{g.label}</span>
                    {g.field && (
                      <span className="tpt__menu-count">{groupCounts[g.key] ?? 0}</span>
                    )}
                    <i className="bi bi-check2 tpt__menu-check" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="tpt__wrap">
          {loading ? (
            <div className="tpt__loading">
              {/* inline: this is the table's own loading state and belongs in
                  the table's own box. Without it, .container-loader covers the
                  viewport and a list refresh reads as the app reloading. */}
              <Loader isLoading inline variant="table" rows={8} cols={6} />
            </div>
          ) : matchCount === 0 ? (
            <p className="tpt__empty">
              {rows.length === 0
                ? "Nothing pending. This team is clear."
                : `Nothing matches “${query}”.`}
            </p>
          ) : view === "summary" ? (
            <table className="tpt__table is-summary">
              <colgroup>
                <col style={{ width: "58px" }} />
                <col />
                <col style={{ width: "84px" }} />
                {breakdown.statusCols.map((s) => (
                  <col key={s} style={{ width: "116px" }} />
                ))}
                <col style={{ width: "108px" }} />
                <col style={{ width: "94px" }} />
              </colgroup>

              <thead>
                <tr>
                  <th className="is-sno" scope="col">S.No</th>
                  <th scope="col">{summaryMode.head}</th>
                  <th className="is-num" scope="col">Total</th>
                  {breakdown.statusCols.map((s) => (
                    <th className="is-num" key={s} scope="col">
                      {s}
                    </th>
                  ))}
                  <th className="is-num" scope="col">Unclaimed</th>
                  <th className="is-num" scope="col">Oldest</th>
                </tr>
              </thead>

              <tbody>
                {breakdown.items.map((row, i) => (
                  <tr className="tpt__row" key={row.label}>
                    <td className="is-sno">
                      <span className="tpt__sno">{i + 1}</span>
                    </td>
                    <td>
                      <span className="tpt__cut" title={row.label}>
                        {row.label}
                      </span>
                    </td>
                    <td className="is-num">
                      <b className="tpt__total">{row.total}</b>
                    </td>

                    {/* A zero prints as a dash. A grid of real 0s reads as
                        data you have to check; a dash reads as "none", which
                        is what it means, and lets the counts stand out. */}
                    {breakdown.statusCols.map((s) => (
                      <td className="is-num" key={s}>
                        {row.byStatus[s] ? (
                          row.byStatus[s]
                        ) : (
                          <span className="tpt__dash">—</span>
                        )}
                      </td>
                    ))}

                    <td className="is-num">
                      {row.unclaimed ? (
                        <span className="tpt__who is-none">{row.unclaimed}</span>
                      ) : (
                        <span className="tpt__dash">—</span>
                      )}
                    </td>
                    <td className="is-num">
                      <span className={`tpt__age${row.oldest > 30 ? " is-stale" : ""}`}>
                        {row.oldest}d
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>

              {/* Sticky at the bottom: the totals are what the breakdown is
                  checked against, and scrolling to a client should not cost
                  you the number you are comparing it to. */}
              <tfoot>
                <tr>
                  <td className="is-sno" />
                  <td>Total</td>
                  <td className="is-num">{matchCount}</td>
                  {breakdown.statusCols.map((s) => {
                    const found = summary.statuses.find(([k]) => k === s);
                    return (
                      <td className="is-num" key={s}>
                        {found ? found[1] : 0}
                      </td>
                    );
                  })}
                  <td className="is-num">{summary.unclaimed}</td>
                  <td className="is-num">{summary.oldest}d</td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <table className="tpt__table">
              <colgroup>
                {columns.map((c) => (
                  <col key={c.key} style={{ width: c.width }} />
                ))}
              </colgroup>

              {/* Sticky, because a column of bare dates two hundred rows down
                  is only a date column while its heading is on screen. */}
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className={c.cls} scope="col">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* One tbody per section rather than one long one. The group
                  heading is a row of the table, so it keeps the columns it
                  spans instead of floating over them, and it sticks BELOW the
                  column heads rather than on top of them. */}
              {sections.map((section) => (
                <tbody key={section.key || "all"}>
                  {section.label && (
                    <tr className="tpt__grouprow">
                      <th colSpan={columns.length} scope="colgroup">
                        <span className="tpt__section-name">{section.label}</span>
                        <span className="tpt__section-count">
                          {section.items.length}
                        </span>
                      </th>
                    </tr>
                  )}

                  {section.items.map((r, i) => {
                    /* Tinted by how the target date stands, the same two
                       colours the grids use. */
                    const state = targetDateState(r.TargetDate);
                    return (
                    <tr
                      className={`tpt__row${
                        state === "overdue"
                          ? " is-target-over"
                          : state === "due"
                            ? " is-target-due"
                            : ""
                      }`}
                      key={r.QueryCode}
                    >
                      {columns.map((c) => (
                        <td key={c.key} className={c.cls}>
                          {renderCell(c.key, r, i + 1)}
                        </td>
                      ))}
                    </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          )}
        </div>

        {/* Read-only, like the one on Task Assignment: this screen is for
            deciding what to do about a task, not for changing it. */}
        <Modal
          show={!!detailTask}
          onClose={() => setDetailTask(null)}
          title="Task Details"
          maxWidth="900px"
        >
          {detailTask && (
            <TaskDetailContent
              task={detailTask}
              timeline={timeline}
              timelineStatus={timelineStatus}
            />
          )}
        </Modal>

        {/* A search that hides 30 of 40 rows should say so — otherwise the
            filtered table looks exactly like a team with ten tasks. */}
        {!loading && rows.length > 0 && (
          <div className="tpt__foot">
            <div className="tpt__foot-meta">
              <span>
                Showing <strong>{matchCount}</strong> of {rows.length}
              </span>
              {matchCount > 0 &&
                (view === "summary" ? (
                  <span>
                    {breakdown.items.length}{" "}
                    {breakdown.items.length === 1
                      ? summaryMode.head.toLowerCase()
                      : summaryMode.many}
                  </span>
                ) : (
                  groupBy !== "none" && (
                    <span>
                      {sections.length} group{sections.length === 1 ? "" : "s"}
                    </span>
                  )
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamPendingTasksView;
